import React, { useEffect, useRef } from "react";
import "./scroll-progress.css";

/**
 * ScrollProgress \u2014 a 2px cyan bar fixed at the top of the viewport
 * whose width tracks the document's scroll progress.
 *
 * Why a single fixed div + CSS scaleX (vs. updating width)?
 *   - scaleX runs on the GPU compositor; width triggers layout.
 *   - One transform write per frame, so we don't fight React's
 *     state model or trigger a re-render on scroll.
 *
 * requestAnimationFrame coalesces scroll events to one update per
 * paint, which is the right rate for a 60fps progress indicator.
 */
const ScrollProgress = () => {
  const ref = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    const update = () => {
      const el = ref.current;
      if (!el) return;
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      const docHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? Math.min(1, scrollTop / docHeight) : 0;
      el.style.transform = `scaleX(${progress})`;
    };

    const onScroll = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <div className="scroll-progress" aria-hidden="true">
      <div className="scroll-progress-bar" ref={ref} />
    </div>
  );
};

export default ScrollProgress;
